import { formatDate } from '@/utils/helpers';
import moment from 'moment';

export const PAYLOAD_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Mapping response mentah form request menjadi format form state/data.
 *
 * @param data
 * @param company
 * @param hospital
 * @param vendor
 * @param ioNumber
 * @returns {*}
 */
export function mapFormRequest(data, company, hospital, vendor, ioNumber) {
  const payments = mapPayments(data);
  const events = mapEvents(data);
  const dateFormat = PAYLOAD_DATE_FORMAT;

  hospital = hospital || {
    h_name: data.hospital,
    h_address: data.hospital_address,
    h_city: data.hospital_city
  };

  vendor = vendor || {
    v_code: data.vendor_code,
    v_name: data.vendor_name,
    v_bank_name: data.bank_name,
    v_bank_branch: data.bank_branch,
    v_bank_account: data.account_number
  };

  const eventTotalAmount = events.reduce((prev, item) => {
    return prev + item.items_total_amount;
  }, 0);

  const form = {
    requestor: {
      region: data.region,
      district: data.district,
      district_manager: data.district_manager
    },
    general_information: {
      created_date: formatDate(data.created_date, dateFormat),
      preval_number: data.preval_number,
      po_number: 'Non PO Activity',
      gl_account: '811112',
      cost_center: '400455',
      io_number: ioNumber,
      amount_of_support: +data.support_amount
    },
    first_party_information: {
      company_name: company,
      company_address: data.company_addres,
      name_of_signatory: data.first_party_name_of_signatory,
      title_of_signatory: data.first_party_title_of_signatory
    },
    second_party_information: {
      hospital_name: hospital,
      hospital_description: data.hospital_description,
      hospital_address: hospital.h_address,
      hospital_city: hospital.h_city,
      hospital_telephone: data.second_party_telephone,
      hospital_fax: data.second_party_fax,
      hospital_email: data.second_party_email,
      vendor_code: vendor,
      vendor_name: vendor.v_name,
      bank_name: vendor.v_bank_name,
      bank_branch: vendor.v_bank_branch,
      bank_account_number: vendor.v_bank_account,
      signatory_name: data.second_party_name_of_signatory,
      signatory_title: data.second_party_title_of_signatory,
      remarks: data.remarks
    },
    agreement_information: {
      date_start: formatDate(data.start_date, dateFormat),
      date_end: formatDate(data.end_date, dateFormat),
      amount_of_support: data.support_amount,
      payment_type: payments.length > 1 ? 2 : 1,
      payments: payments
    },
    event_information: {
      total_amount: eventTotalAmount,
      events
    },
    supporting_documents: {
      invoice: {
        name: data.document.invoice_name,
        url: data.document.invoice_url
      },
      proposal: {
        name: data.document.proposal_name,
        url: data.document.proposal_url
      },
      mou: {
        name: data.document.mou_name,
        url: data.document.mou_url
      },
      summary_of_prevalidation: {
        name: data.document.summary_of_prevalidation_name,
        url: data.document.summary_of_prevalidation_url
      }
    },
    signature_information_of_first_party: {
      region_address: '',
      phone_number: '',
      fax_number: '',
      email: '',
      pic_name: ''
    },
    saveDraft: false
  };

  return { form, events, payments };
}

/**
 * Mengubah format date dari 'DD/MM/YYYY' menjadi 'YYYY-MM-DD'.
 *
 * @param date
 * @param fromFormat
 * @param toFormat
 * @returns {string}
 */
export function reformatDate(
  date,
  fromFormat = 'DD/MM/YYYY',
  toFormat = 'YYYY-MM-DD'
) {
  return moment(date, fromFormat).format(toFormat);
}

/**
 * Mapping event menjadi bentuk object yg disesuaikan.
 *
 * @param data
 * @param noPurposeWith
 * @returns {*}
 */
export function mapEvents(data, noPurposeWith = false) {
  const purposeWithData = item =>
    noPurposeWith
      ? {}
      : {
          purpose_with: {
            peserta: item.peserta,
            participant: item.participants,
            bidang: item.bidang,
            subject: item.area
          }
        };

  return data.event.map(item => {
    const date = item.date_of_event.split('||');
    const items = item.items.map(eventItem => ({
      item: eventItem.item,
      price: eventItem.price,
      quantity: eventItem.quantity,
      frequency: eventItem.frequency,
      amount: eventItem.amount
    }));
    const { frequency_of_activity } = item;
    const totalAmount = items.reduce((prev, itemData) => {
      return prev + +itemData.amount;
    }, 0);
    const itemsTotalAmount = totalAmount * +frequency_of_activity;

    return {
      purpose: item.purpose_of_event,
      activities: item.activities_of_event,
      name: item.name_of_event,
      venue: item.venue_of_event,
      date_start: reformatDate(date[0]),
      date_end: reformatDate(date[1]),
      frequency: frequency_of_activity,
      items_total_amount: itemsTotalAmount,
      items,
      ...purposeWithData(item)
    };
  });
}

/**
 * Mapping payment.
 *
 * @param data
 * @returns {*}
 */
export function mapPayments(data) {
  return data.payment.map(item => ({
    percentage: item.percent,
    amount: item.int
  }));
}

/**
 * Mengubah format form request data (payload) sesuai dengan format API.
 *
 * @param form
 * @param events
 * @param payments
 * @param isEditing
 * @param hospitals
 * @param revision
 * @returns {any}
 */
export function formatFormRequestData({
  form,
  events,
  payments,
  isEditing,
  revision
}) {
  const data = JSON.parse(JSON.stringify(form));

  data.agreement_information.payments = payments;
  data.event_information.events = events;

  delete data.event_information.total_amount;
  delete data.agreement_information.payment_type;
  delete data.general_information.io_description;

  // set io number
  const ioNumber = data.general_information.io_number;
  data.general_information.io_number =
    typeof ioNumber === 'string' ? ioNumber : ioNumber.io_number;

  // set vendor code
  const vendorCode = data.second_party_information.vendor_code;
  data.second_party_information.vendor_code =
    typeof vendorCode === 'string' ? vendorCode : vendorCode.v_code;

  // set hospital name
  const hospital = data.second_party_information.hospital_name;
  data.second_party_information.hospital_name =
    typeof hospital === 'string' ? hospital : hospital.h_name;

  // set company
  const company = data.first_party_information.company_name;

  data.first_party_information.company_name = company ? company.c_name : '';
  data.first_party_information.company_address = company
    ? company.c_address
    : '';

  // delete all event mapping

  if (isEditing || revision) {
    delete data.saveDraft;
  }

  if (revision) {
    data.revision = revision;
  }

  return data;
}
